import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { playClick } from "../../lib/sounds";
import type { PreviousConversation } from "./chatTypes";
import { usePreviousChatMotion } from "./usePreviousChatMotion";

export const PEEK_EDGE_HEIGHT = 40;
export const PREVIOUS_CHAT_BOTTOM_GAP = 44;
export const PREVIOUS_CHAT_OUTLINE_WIDTH = 0.5;
const HOVER_PEEK = 14;
const OPEN_SNAP_RATIO = 0.65;
const SCROLL_GESTURE_GAP = 180;

export function usePreviousChat({
  previous,
  departing,
  currentScrollRef,
  bottomGap = PREVIOUS_CHAT_BOTTOM_GAP,
}: {
  previous?: PreviousConversation;
  departing: boolean;
  currentScrollRef: React.RefObject<HTMLDivElement | null>;
  bottomGap?: number;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const { stackRef, getOffset, jumpTo, settle, stop, setScrollOffset } =
    usePreviousChatMotion();
  const previousScrollRef = useRef<HTMLDivElement>(null);
  const edgeRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    startOffset: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const wheelDismissedAtRef = useRef<number | null>(null);
  const [mode, setMode] = useState<"closed" | "dragging" | "open">("closed");
  const [atTop, setAtTop] = useState(true);
  const [previousScrolledUp, setPreviousScrolledUp] = useState(false);
  const [departedId, setDepartedId] = useState<string | null>(null);
  const [previousId, setPreviousId] = useState(previous?.id);
  if (previousId !== previous?.id) {
    setPreviousId(previous?.id);
    setMode("closed");
    setDepartedId(null);
    setPreviousScrolledUp(false);
  }
  const isDeparting = !!previous && departing && departedId !== previous.id;
  const revealed = mode !== "closed" && !isDeparting && atTop;

  const peekHeight = useCallback(() => {
    return Math.max(
      0,
      (viewportRef.current?.getBoundingClientRect().height ?? 0) -
        bottomGap -
        PEEK_EDGE_HEIGHT
    );
  }, [bottomGap]);

  const close = useCallback(() => {
    setMode("closed");
    settle(0);
  }, [settle]);

  function finishDrag(event: React.PointerEvent<HTMLButtonElement>) {
    if (!dragRef.current) return;
    const moved = dragRef.current.moved;
    dragRef.current = null;
    if (moved) {
      suppressClickRef.current = true;
      const limit = peekHeight();
      if (
        event.type === "pointerup" &&
        limit > 0 &&
        getOffset() >= limit * OPEN_SNAP_RATIO
      ) {
        setMode("open");
        settle(limit);
        playClick();
      } else {
        close();
      }
    }
  }

  function syncPreviousScroll() {
    const scroll = previousScrollRef.current;
    if (scroll) {
      setPreviousScrolledUp(
        scroll.scrollHeight -
          scroll.clientHeight -
          Math.max(0, scroll.scrollTop) >
          1
      );
    }
  }

  useLayoutEffect(() => {
    const scroll = previousScrollRef.current;
    if (scroll) {
      scroll.scrollTop = previous?.scrollTop ?? scroll.scrollHeight;
      syncPreviousScroll();
    } else {
      jumpTo(0);
    }
  }, [previous?.id, previous?.scrollTop, jumpTo]);

  useLayoutEffect(() => {
    const scroll = currentScrollRef.current;
    if (!previous?.id || !scroll) return;
    let lastWheelTime = -Infinity;
    let pulledThisGesture = false;
    function pullPrevious(distance: number) {
      if (
        pulledThisGesture ||
        mode !== "closed" ||
        isDeparting ||
        dragRef.current ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        return;
      pulledThisGesture = true;
      const remaining = Math.max(0, HOVER_PEEK - getOffset());
      jumpTo(HOVER_PEEK - remaining * Math.exp(-distance / 40));
      settle(0, 1.2);
    }
    let lastScrollTop = Math.max(0, scroll.scrollTop);
    const syncScroll = () => {
      const scrollTop = Math.max(0, scroll.scrollTop);
      setScrollOffset(
        Math.min(PEEK_EDGE_HEIGHT + PREVIOUS_CHAT_OUTLINE_WIDTH, scrollTop)
      );
      setAtTop(scrollTop <= 1);
      if (scrollTop > 1) {
        pulledThisGesture = false;
        dragRef.current = null;
        jumpTo(0);
        setMode("closed");
      } else if (lastScrollTop > 1) {
        pullPrevious(lastScrollTop - scrollTop);
      }
      lastScrollTop = scrollTop;
    };
    const onWheel = (event: WheelEvent) => {
      if (
        event.ctrlKey ||
        event.deltaY >= 0 ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
      )
        return;
      for (
        let target = event.target instanceof Element ? event.target : null;
        target && target !== scroll;
        target = target.parentElement
      ) {
        if (
          target.scrollTop > 0 &&
          /auto|scroll/.test(getComputedStyle(target).overflowY)
        )
          return;
      }
      const now = performance.now();
      if (now - lastWheelTime > SCROLL_GESTURE_GAP) pulledThisGesture = false;
      lastWheelTime = now;
      if (scroll.scrollTop > 1) return;
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? scroll.clientHeight
            : 1;
      pullPrevious(-event.deltaY * unit);
    };
    syncScroll();
    scroll.addEventListener("scroll", syncScroll, { passive: true });
    scroll.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      scroll.removeEventListener("scroll", syncScroll);
      scroll.removeEventListener("wheel", onWheel);
    };
  }, [
    currentScrollRef,
    previous?.id,
    mode,
    isDeparting,
    getOffset,
    jumpTo,
    setScrollOffset,
    settle,
  ]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!previous?.id || !viewport) return;
    let lastWheelTime = -Infinity;
    const onWheel = (event: WheelEvent) => {
      if (
        event.ctrlKey ||
        event.deltaY === 0 ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
      )
        return;
      const now = performance.now();
      const newGesture = now - lastWheelTime > SCROLL_GESTURE_GAP;
      lastWheelTime = now;
      if (event.deltaY < 0) return;
      const dismissedAt = wheelDismissedAtRef.current;
      if (dismissedAt !== null && now - dismissedAt < SCROLL_GESTURE_GAP) {
        event.preventDefault();
        event.stopPropagation();
        wheelDismissedAtRef.current = now;
        return;
      }
      wheelDismissedAtRef.current = null;
      if (mode !== "open" || isDeparting || dragRef.current) return;
      const surface = edgeRef.current?.parentElement;
      if (
        !surface ||
        !(event.target instanceof Element) ||
        !surface.contains(event.target)
      )
        return;
      for (
        let target: Element | null = event.target;
        target && target !== surface;
        target = target.parentElement
      ) {
        if (
          target.scrollHeight - target.clientHeight - target.scrollTop > 1 &&
          /auto|scroll/.test(getComputedStyle(target).overflowY)
        )
          return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (!newGesture) return;
      wheelDismissedAtRef.current = now;
      close();
    };
    viewport.addEventListener("wheel", onWheel, {
      capture: true,
      passive: false,
    });
    return () => viewport.removeEventListener("wheel", onWheel, true);
  }, [previous?.id, mode, isDeparting, close]);

  useEffect(() => {
    if (!previous?.id) return;
    const onFocus = (event: FocusEvent) => {
      if (
        event.target instanceof Node &&
        !viewportRef.current?.contains(event.target)
      )
        close();
    };
    const onBlur = () => {
      dragRef.current = null;
      close();
    };
    document.addEventListener("focusin", onFocus);
    window.addEventListener("blur", onBlur);
    const resize = new ResizeObserver(() => {
      if (mode === "open") settle(peekHeight());
    });
    if (viewportRef.current) resize.observe(viewportRef.current);
    return () => {
      document.removeEventListener("focusin", onFocus);
      window.removeEventListener("blur", onBlur);
      resize.disconnect();
    };
  }, [previous?.id, mode, close, settle, peekHeight]);

  const viewportHandlers: Pick<
    React.HTMLAttributes<HTMLDivElement>,
    "onKeyDown"
  > = {
    onKeyDown: (event) => {
      if (event.key === "Escape" && revealed) {
        event.preventDefault();
        event.stopPropagation();
        close();
        edgeRef.current?.focus({ preventScroll: true });
      }
    },
  };
  const surfaceHandlers: Pick<
    React.HTMLAttributes<HTMLDivElement>,
    "onAnimationEnd" | "onPointerEnter" | "onPointerLeave"
  > = {
    onAnimationEnd: (event) => {
      if (
        previous &&
        event.target === event.currentTarget &&
        !event.pseudoElement
      )
        setDepartedId(previous.id);
    },
    onPointerEnter: (event) => {
      if (
        event.pointerType === "mouse" &&
        mode === "closed" &&
        atTop &&
        !isDeparting &&
        !dragRef.current
      )
        settle(HOVER_PEEK);
    },
    onPointerLeave: () => {
      if (mode === "closed" && !dragRef.current) settle(0);
    },
  };
  const edgeHandlers: Pick<
    React.HTMLAttributes<HTMLButtonElement>,
    "onPointerDownCapture" | "onPointerMove" | "onClick"
  > = {
    onPointerDownCapture: (event) => {
      if (isDeparting || !event.isPrimary || event.button !== 0) return;
      stop();
      suppressClickRef.current = false;
      dragRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startOffset: getOffset(),
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const delta = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(delta) < 5) return;
      drag.moved = true;
      setMode("dragging");
      const distance = Math.max(0, drag.startOffset + delta);
      const limit = peekHeight();
      const offset =
        distance <= limit * 0.8
          ? distance
          : limit * 0.8 +
            limit *
              0.2 *
              (1 - Math.exp(-(distance - limit * 0.8) / (limit * 0.2 || 1)));
      jumpTo(offset);
    },
    onClick: (event) => {
      if (suppressClickRef.current && event.detail !== 0) {
        suppressClickRef.current = false;
        return;
      }
      if (mode === "open") close();
      else {
        setMode("open");
        settle(peekHeight());
      }
    },
  };
  return {
    viewportRef,
    stackRef,
    previousScrollRef,
    edgeRef,
    mode,
    atTop,
    isDeparting,
    revealed,
    previousScrolledUp,
    viewportHandlers,
    surfaceHandlers,
    edgeHandlers: {
      ...edgeHandlers,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
      onLostPointerCapture: finishDrag,
    },
    onPreviousScroll: syncPreviousScroll,
  };
}
